import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { ITravel } from "../Models";
import dayjs from "dayjs";
import { getApiCallState, useStateSelector } from "./Model";
import { NumberSelector } from "../Components/NumberSelector";
import { useDispatch } from "react-redux";
import { loadAvailableTimes } from "../Actions";
import { krogenSlots } from "../TravelDetails/KrogenOptionsHelpers";
import { clearAvailableTimes } from "../OmbordPersonal/Actions";
import { bookTable } from "./Actions";
import { clearApiCallState } from "../ApiCalls/Actions";
import { findRelevantLeg } from "./App";
import { getStrings } from "../../Shared/Reservation/StringResources";

const maxPassengerCount = 12;
const strings = getStrings().component.tableBooking;
const commonstrings = getStrings().component.common;

export const BookTableModal = (props: { show: boolean, slot: "lunch"|"dinner", travel: ITravel, onClosed: (tableBooked: boolean) => void }) => {

	const dispatch = useDispatch();

	const [numberOfGuests, setNumberOfGuests] = useState(1);
	const [selectedOptions, setSelectOptions] = useState<{option: string, count: number}[]>([]);
	const [selectedTime, setSelectedTime] = useState<string>();

	const apicalls = getApiCallState();
	const bookingState = apicalls.getLoadingState("bookTable");
	const loadTimesState = apicalls.getLoadingState("loadAvailableTimes");

	const state = useStateSelector(state => state.booking);

	const options = props.slot === "dinner" ? state.dinnerOptions : state.lunchOptions

	const slot = krogenSlots.find(x => x.slotName === (props.slot === "lunch" ? "lunch2" : "middag1"));

	const slotOffsetDays = props.slot === "dinner" ? 0 : 1;
	const date = dayjs(props.travel?.departureDate).add(slotOffsetDays, "day").startOf("day");
	const leg = findRelevantLeg(props.travel);

	const optionCount = selectedOptions.reduce((sum, curr) => curr.count+sum, 0);
	const remainingCount = Math.max(numberOfGuests - optionCount, 0);

	const optionsValid = optionCount === numberOfGuests;
	const enableBooking = optionsValid && selectedTime && state.availableTimes;

	const optionCountChanged = (option: string, count: number) => {
		let temp = [...selectedOptions];
		const idx = temp.findIndex(x => x.option === option);
		if (idx >= 0)
			temp[idx].count = count;
		else if (count > 0)
			temp.push({ option, count });
		setSelectOptions(temp);
	}

	const findTimes = () => {
		if (props.show && numberOfGuests && date && date.isValid())
		{
			setSelectedTime(null);
			dispatch(loadAvailableTimes(leg, numberOfGuests, "", slot?.startTime, slot?.endTime, slotOffsetDays));
		}
	}

	const onBookClicked = () => {
		const menuOptions = selectedOptions.filter(x => x.count > 0).map(x => `${x.option} x ${x.count}`);

		dispatch(bookTable(leg, numberOfGuests, selectedTime, date, state.customerDetails, menuOptions, props.travel.id, props.slot));
	};

	const close = (tableBooked: boolean = false) => {
		dispatch(clearApiCallState("loadAvailableTimes"));
		dispatch(clearAvailableTimes());
		setSelectedTime(null);
		props.onClosed(tableBooked);
	}

	useEffect(() => {
		if (bookingState === "loaded")
			close(true);
	}, [bookingState]);

	useEffect(() => {
		dispatch(clearAvailableTimes());
	}, [numberOfGuests, props.slot, props.show])

	return <Modal show={props.show} centered>
		<Modal.Header>
			<Modal.Title>{strings.modalTitle}</Modal.Title>
		</Modal.Header>
		<Modal.Body className="book-table-dialog">
			<h4>{props.travel?.originStation?.name} ➔ {props.travel?.destinationStation?.name}</h4>
			<h5>{date?.format("YYYY-MM-DD")}, {props.slot}</h5>

			<Form.Group controlId="guestCount" className="select-persons">
				<Form.Label>{strings.personsLabel}</Form.Label>
				<Form.Control as="select" size="sm" custom value={numberOfGuests}
						onChange={e => setNumberOfGuests(parseInt(e.currentTarget.value))}>
					{Array.from({length: maxPassengerCount}).map((_, i) => <option value={i+1} key={i}>{i+1}</option>)}
				</Form.Control>
			</Form.Group>

			{ options && <div className="option-selector">
				<strong>{strings.menuOptions}</strong>
				{options.map((opt, idx)=> <React.Fragment key={idx}>
					<Row className="mb-1">
						<Col xs={8} sm={9}><label htmlFor={`option_${idx}`}>{opt}</label></Col>
						<Col xs={4} sm={3}><NumberSelector inputId={`option_${idx}`} min={0} max={(selectedOptions.find(x => x.option === opt)?.count || 0) + remainingCount} onValueChanged={n => optionCountChanged(opt, n)} /></Col>
					</Row>
				</React.Fragment>)}
			</div>}

			<Alert variant="danger" show={!optionsValid} className="mt-3">{strings.menuError}</Alert>
			<Alert variant="danger" show={loadTimesState === "error"} className="mt-3">{strings.errorFetchingTables}</Alert>
			<Alert variant="warning" show={loadTimesState === "loaded" && !state.availableTimes?.length} className="mt-3">{strings.noAvailableTimes}</Alert>

			{!state.availableTimes && loadTimesState !== "error" && <Button variant="outline-primary" disabled={!optionsValid} onClick={findTimes}>{strings.fetchTables}</Button> }

			{state.availableTimes?.length > 0 && <div>
				<strong>{strings.availableTables}</strong>
				<div className="available-times mt-2">
					{ state.availableTimes.map(x => <Button variant="outline-primary" className={`mr-2 mb-2 ${x === selectedTime ? "active" : ""}`} key={x} onClick={() => setSelectedTime(x)}>{x}</Button>) }
				</div>
			</div>}

			<p className="mt-2">{strings.modalInfoText}</p>

		</Modal.Body>
		<Modal.Footer>
			<Button variant="secondary" disabled={bookingState === "loading"} onClick={() => close(false)}>{commonstrings.abort}</Button>
			<Button variant="primary" disabled={!enableBooking && bookingState !== "loading"} onClick={onBookClicked}>{strings.submitText}</Button>
		</Modal.Footer>
	</Modal>
};